import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';
import TechnicalSpecs from '../components/product/TechnicalSpecs';
import CompatibilityMatrix from '../components/product/CompatibilityMatrix';

const { FiArrowLeft, FiExternalLink, FiDownload, FiShare2 } = FiIcons;

const ProductDetail = () => {
  const { id } = useParams();
  const { getProductById, getCategoryName } = useDatabase();
  const product = getProductById(id);

  if (!product) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Produkt nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/search"
              className="flex items-center space-x-2 text-gray-600 hover:text-black transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              <span>Zurück zur Suche</span>
            </Link>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Datenblatt</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              <span>Teilen</span>
            </button>
          </div>
        </div>

        {/* Product Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {product.manufacturer_sku}
                  </h1>
                  <p className="text-gray-600 mb-2">{product.manufacturer}</p>
                  <span className="inline-block px-3 py-1 bg-red-50 text-red-800 rounded-full text-sm font-medium border border-red-100">
                    {getCategoryName(product.etim_class_code)}
                  </span>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${
                    product.availability === 'In Stock' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {product.availability === 'In Stock' ? 'Auf Lager' : 'Nicht verfügbar'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">Artikelnummer</p>
                  <p className="font-mono text-sm text-gray-900">{product.manufacturer_sku}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">EAN/GTIN</p>
                  <p className="font-mono text-sm text-gray-900">{product.ean_gtin || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ETIM-Klasse</p>
                  <p className="font-mono text-sm text-gray-900">{product.etim_class_code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Letzte Aktualisierung</p>
                  <p className="text-sm text-gray-900">{new Date(product.updated_at).toLocaleDateString('de-DE')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-3">Schnellaktionen</h3>
              <div className="space-y-2">
                <button className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors shadow-sm font-medium">
                  Anfrage senden
                </button>
                <button className="w-full bg-white border border-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors">
                  Zur Merkliste
                </button>
                <button className="w-full flex items-center justify-center space-x-2 text-gray-600 hover:text-black py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors">
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  <span>Herstellerseite</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <TechnicalSpecs specifications={product.specifications} category={product.etim_class_code} />

        {/* Compatibility Matrix (for seals) */}
        {product.etim_class_code === 'EC002306' && product.specifications.compatibility_matrix && (
          <CompatibilityMatrix matrix={product.specifications.compatibility_matrix} />
        )}

        {/* Additional Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Zusätzliche Informationen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Anwendungshinweise</h3>
              <p className="text-gray-600 text-sm">
                Dieses Produkt entspricht den aktuellen Industriestandards und ist für den 
                professionellen Einsatz in der Maschinenbauindustrie konzipiert.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Lieferinformationen</h3>
              <p className="text-gray-600 text-sm">
                Standardlieferzeit: 1-3 Werktage. Express-Versand verfügbar. 
                Mindestbestellmenge: 1 Stück.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductDetail;